import { gql, useMutation, useQuery } from '@apollo/client'
import CancelIcon from '@mui/icons-material/Cancel'
import FileCopyIcon from '@mui/icons-material/FileCopy'
import {
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import Modal from 'components/Modal'
import { useGetMe } from 'components/Session/hooks'
import React, { useState } from 'react'
import { Company, Dataroom } from 'utils/__generated__/graphql'

import { DataroomDataType, DataroomForm } from '../Create'
import { DATAROOM_BASE_QUERIES, DATAROOM_FRAGMENT } from '../constants'

const CLONE_DATAROOM = gql`
  ${DATAROOM_FRAGMENT}
  mutation CLONE_DATAROOM($dataroom: DataroomInput!) {
    cloneDataroom(dataroom: $dataroom) {
      ...DataroomFragment
    }
  }
`

function Clone({ dataroom }: { dataroom: Dataroom }) {
  const [open, setOpen] = useState<boolean>(false)
  const { data: meData } = useGetMe()

  const theme = useTheme()

  const handleOpen = () => setOpen(true)
  const handleClose = () => setOpen(false)

  const [cloneDataroom] = useMutation(CLONE_DATAROOM)

  const handleDataroom = (data: DataroomDataType) => {
    cloneDataroom({
      variables: {
        dataroom: {
          id: dataroom.id,
          user: meData?.me?.id,
          company: meData?.me?.company?.id,
          isAdminDataroom: true,
          ...data,
        },
      },
      onCompleted: handleClose,
      update(cache, { data: { cloneDataroom } }) {
        cache.modify({
          fields: {
            findDatarooms(existingDatarooms = []) {
              const newDataroomRef = cache.writeFragment({
                data: cloneDataroom,
                fragment: gql`
                  fragment NewDataroom on Dataroom {
                    id
                  }
                `,
              })
              return [newDataroomRef, ...existingDatarooms]
            },
          },
        })
      },
    })
  }

  const { loading, data } = useQuery(DATAROOM_BASE_QUERIES, {
    variables: { siteFilter: { isPublished: true }, companyFilter: {} },
    skip: !open,
  })

  const sites = data?.sites || []
  const companies = data?.companies || []

  const formValues = {
    site: dataroom.site?.id || '',
    name: `${dataroom.name || ''} (Copy)`,
    targetCompanies: (dataroom.targetCompanies || []).map(
      (company: Company | null) => company?.id || ''
    ),
  }

  return (
    <React.Fragment>
      <Modal open={open} width="75%" maxWidth={720}>
        <Stack
          direction="row"
          sx={{ alignItems: 'center', justifyContent: 'space-between' }}
        >
          <Typography
            sx={{ ...theme.typography.h5, color: theme.palette.text.primary }}
          >
            Clone - {dataroom.name}
          </Typography>
          <CancelIcon
            onClick={handleClose}
            sx={{ cursor: 'pointer', color: theme.palette.error.main }}
          />
        </Stack>
        <Divider sx={{ mt: 1, mb: 1 }} />
        {loading ? (
          <Stack sx={{ alignItems: 'center', p: 4 }}>
            <CircularProgress size={20} />
          </Stack>
        ) : (
          <DataroomForm
            values={formValues}
            onDataroom={handleDataroom}
            sites={sites}
            companies={companies}
          />
        )}
      </Modal>
      <IconButton onClick={handleOpen}>
        <Tooltip title="Clone" placement="top">
          <FileCopyIcon />
        </Tooltip>
      </IconButton>
    </React.Fragment>
  )
}

export default Clone
